import {
  Box,
  Heading,
  HStack,
  IconButton,
  Text,
  useEditable,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FiEdit2 } from "react-icons/fi";
import { KYDTicket, KYDTicketType } from "@common/interfaces/KYDTicket";
import { useAlgoliaClient } from "../../hooks/useAlgoliaClient";
import AttendeesFilter from "./AttendeesFilter";
import { InstantSearch } from "react-instantsearch";
import AttendeesTable from "./AttendeesTable";
import RefundDialog, {
  RefundDialogContext,
  RefundDialogValues,
  withRefundDialog,
} from "./RefundDialog";
import actions from "../../../../actions";
import {
  AlgoliaAttendeeObject,
  TicketTransaction,
} from "@common/interfaces/Algolia";
import { useDialog } from "../../../../Common/Dialog";
import EditAttendeeDialog, {
  EditAttendeeDialogContext,
  EditAttendeeDialogValues,
  withEditAttendeeDialog,
} from "./EditAttendeeDialog";
import { EventDetailAttendeesSection } from "@common/interfaces/KYDEvent";
import { SectionProps } from "../../sections/sectionsComponents";
import { set } from "react-hook-form";

export type AlgoliaAttendeeWithTickets = AlgoliaAttendeeObject & {
  tickets: Array<
    KYDTicket & {
      sale: {
        display_total: string;
        purchased_at: string;
        source: string;
        tender_type: string;
        payment_id: string;
        created_at: string;
        purchased_by: string;
      };
    }
  >;
  transactions?: TicketTransaction[];
};

const AttendeesSection = ({
  data,
  isLoading = false,
  permissions,
}: SectionProps<EventDetailAttendeesSection["data"]>) => {
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const { open: openRefundDialog, close: closeRefundDialog } =
    useContext(RefundDialogContext);
  const { open: openEditAttendeeDialog, close: closeEditAttendeeDialog } =
    useContext(EditAttendeeDialogContext);
  const [refundingAttendee, setRefundingAttendee] =
    useState<AlgoliaAttendeeWithTickets | null>(null);
  const [editingAttendee, setEditingAttendee] =
    useState<AlgoliaAttendeeWithTickets | null>(null);

  const readOnly = !permissions.includes("write");

  const [objectIdsRefreshing, setObjectIdsRefreshing] = useState<
    Record<string, boolean>
  >({});

  const [isEditingOrRefunding, setIsEditingOrRefunding] = useState(false);

  const algolia = useAlgoliaClient(
    data.algolia,
    {},
    {
      showResultsByDefault: true,
    }
  );
  const toast = useToast();
  const dialog = useDialog();

  const onOpenRefundModal = useCallback(
    async (attendee: AlgoliaAttendeeObject) => {
      setIsLoadingTickets(true);
      fetchAttendeeDetail(attendee.id)
        .then((data) => {
          setRefundingAttendee({
            ...attendee,
            tickets: data.tickets,
            transactions: data.transactions,
          });
        })
        .finally(() => {
          setIsLoadingTickets(false);
        });
      openRefundDialog({
        title: "Refund",
        text: "Select the amount of tickets to refund",
      });
    },
    [refundingAttendee]
  );

  const fetchAttendeeDetail = useCallback(
    async (attendee_id: string) => {
      const result = await actions.ADMIN.fetchAttendeeDetail(
        data.id,
        attendee_id
      );

      return result;
    },
    [data.id]
  );
  const onOpenEditAttendeeModal = useCallback(
    async (attendee: AlgoliaAttendeeObject) => {
      setIsLoadingTickets(true);
      fetchAttendeeDetail(attendee.id)
        .then((data) => {
          setEditingAttendee({
            ...attendee,
            tickets: data.tickets,
            transactions: data.transactions,
          });
        })
        .finally(() => {
          setIsLoadingTickets(false);
        });
      openEditAttendeeDialog({
        title: "Edit attendee",
        text: "",
      });
    },
    [refundingAttendee]
  );

  const setRefreshing = useCallback(
    (objectId: string, refreshing: boolean) => {
      setObjectIdsRefreshing((refreshingMap) => {
        return {
          ...refreshingMap,
          [objectId]: refreshing,
        };
      });
    },
    [setObjectIdsRefreshing]
  );

  const pollAlgoliaForUpdate = useCallback(
    ({ objectID, maxTries = 10 }: { objectID: string; maxTries: number }) => {
      const dateOfUpdate = new Date();
      let tries = 0;

      const refreshInterval = setInterval(async function () {
        if (tries === maxTries) {
          clearTimeout(refreshInterval);
          setRefreshing(objectID, false);
          return;
        }

        setRefreshing(objectID, true);
        try {
          const objectInAlgolia = await algolia.algoliaClient?.getObject(
            {
              indexName: data.algolia.index_name,
              objectID: objectID,
            },
            {
              cacheable: false,
            }
          );

          const updateObjectDate = new Date(
            objectInAlgolia.updated_at as string
          );

          if (updateObjectDate >= dateOfUpdate) {
            clearInterval(refreshInterval);
            setRefreshing(objectID, false);
            console.log("Found the update", objectInAlgolia);
            await algolia.resetClient();
          }
        } catch (e) {
          console.error(e);
          clearInterval(refreshInterval);
          setRefreshing(objectID, false);
        }

        tries = tries + 1;
      }, 1000);
    },
    [algolia, data]
  );

  const onRefund = useCallback(
    async (refundData: {
      tickets: { ticket_type_id: string; id: string }[];
      retain_ticket: boolean;
      user_id: string;
      objectID: string;
    }) => {
      try {
        setIsEditingOrRefunding(true);
        const response = await actions.ADMIN.refundTickets(data.id, refundData);

        toast({
          title: "Refund initiated",
          description:
            "The patron will recieve an email notification about this refund",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (e) {
        dialog({
          title: "Error",
          text: e.message,
        });
      }
      setIsEditingOrRefunding(false);
      setRefundingAttendee(null);
      closeRefundDialog();

      pollAlgoliaForUpdate({
        objectID: refundData.objectID,
        maxTries: 10,
      });
    },
    [algolia]
  );

  const onEditAttendee = useCallback(
    async (editData: EditAttendeeDialogValues) => {
      try {
        setIsEditingOrRefunding(true);
        const response = await actions.ADMIN.updateAttendeeMetadata(data.id, {
          id: editData.id,
          notes: editData.notes,
          tags: editData.tags,
        });

        toast({
          title: "Attendee edited",
          description:
            "Attendee edited with success, it might take a few seconds before the reflect in here",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (e) {
        dialog({
          title: "Error",
          text: e.message,
        });
      }
      setIsEditingOrRefunding(false);
      setEditingAttendee(null);
      closeEditAttendeeDialog();

      pollAlgoliaForUpdate({
        objectID: editData.objectID,
        maxTries: 10,
      });
    },
    [algolia, pollAlgoliaForUpdate]
  );

  const allTicketTypes = useMemo(() => {
    if (!data.system_ticket_type) return data.ticket_types;

    const guestlistTicketType = {
      ...data.system_ticket_type,
      name: "Guestlist",
    };

    return [...data.ticket_types, guestlistTicketType];
  }, [data.ticket_types, data.system_ticket_type]);

  return (
    <VStack w="full">
      <RefundDialog
        user_id={refundingAttendee?.id}
        objectID={refundingAttendee?.objectID}
        tickets={refundingAttendee?.tickets}
        ticket_types={data.ticket_types}
        isLoading={isLoading || isEditingOrRefunding || isLoadingTickets}
        onSave={onRefund}
      />
      <EditAttendeeDialog
        isLoading={isLoading || isEditingOrRefunding || isLoadingTickets}
        ticketTypes={data.ticket_types}
        timezone={data.timezone}
        onSave={onEditAttendee}
        editingAttendee={editingAttendee}
      />
      <HStack w="full">
        <Heading size="sm">Attendees</Heading>
      </HStack>
      {algolia.algoliaClient ? (
        <InstantSearch
          key={algolia.version}
          future={{
            preserveSharedStateOnUnmount: false,
          }}
          indexName={data.algolia.index_name}
          searchClient={algolia.algoliaClient}
        >
          <VStack spacing={4} w="full">
            <AttendeesFilter ticketTypes={allTicketTypes} />
            <Box
              w="full"
              overflowX={
                (data?.ticket_types || []).length === 0 ? "inherit" : "scroll"
              }
            >
              <AttendeesTable
                readOnly={readOnly}
                refreshingObjectIds={objectIdsRefreshing}
                ticket_types={data.ticket_types}
                onClickRefund={onOpenRefundModal}
                onEditAttendee={onOpenEditAttendeeModal}
              />
            </Box>
          </VStack>
        </InstantSearch>
      ) : null}
    </VStack>
  );
};

export default withEditAttendeeDialog(withRefundDialog(AttendeesSection));
